/**
 * Header, strip, content, bottom tabs — on `bg-canvas`, separated by 1px
 * gutters, and the page itself never scrolls.
 *
 * That last part is a layout invariant, not a preference. `body` is
 * `overflow: hidden` (base.css) and every panel scrolls its own body, because a
 * page that can scroll as a whole puts the header and the countdown out of view
 * — and the countdown is on screen for less time than it takes to read a
 * paragraph. Sixty times an hour.
 *
 * Below 1120px the ticket has no column and the sticky bottom bar takes over
 * order entry: it is what opens the sheet. `bottomBar` is that bar, and it sits
 * above the home indicator via `safe-area-inset-bottom`. Below 768px the layout
 * also collapses to a single column: strip → chart → a book panel tabbed
 * Orderbook · Trades · Positions · Orders → sticky UP/DOWN → sheet.
 *
 * **The bottom tables and the roll's secondary card are not slots here, and
 * used to be.** They spanned the whole width, which meant the ticket column
 * stopped 240px above the bottom of the page and the order form was cut off
 * mid-quote. Where a panel ends horizontally is `<PanelGrid>`'s question, so
 * both moved there: the tables now stop at the ticket column's edge and the
 * ticket runs the full height.
 */

import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

export interface AppShellProps {
  header: ReactNode
  /**
   * The connection banner (`docs/07` §3), between the header and the strip.
   *
   * Passed rather than mounted here because `<AppShell>` is a layout component
   * and `<ConnectionBanner>` needs the runtime — the gallery renders this shell
   * with no provider above it. Every route that has a runtime passes one.
   *
   * **It floats; it does not take a row.** It used to be a flex child here, so
   * every `Reconnecting…` pushed the strip, the chart, the book and the ticket
   * down by its height and `Reconnected.` pulled them back two seconds later —
   * two moves of every click target per outage, and traders reported the
   * misclicks. The slot is now a zero-height anchor, the banner hangs from it
   * over the top of the strip, and nothing below it ever moves. A message may
   * cover something; it may never push it.
   */
  notice?: ReactNode
  strip?: ReactNode
  children: ReactNode
  /** The sticky UP/DOWN bar. Shown below 1120px — wherever the ticket is a
   *  sheet, because this is the only thing that opens it. */
  bottomBar?: ReactNode
  className?: string
}

export function AppShell({ header, notice, strip, children, bottomBar, className }: AppShellProps) {
  return (
    <div className={cx('flex h-full flex-col gap-px bg-bg-canvas', className)}>
      {header}
      {/* `z-[15]`: above the strip and the chart's own `z-10` layer, below the
          header's popovers (the tray at 20, the account menu at 30), which may
          open across it. */}
      {notice ? <div className="relative z-[15] -mt-px h-0">{notice}</div> : null}
      {strip}
      {/* The one exception to "the page never scrolls": below 768px. Only this
          element scrolls, so the header above it and the sticky buttons below
          it stay put, and the chart, the rail and the book stack at their own
          heights rather than sharing a phone's height between them — which
          left an iPhone SE one row of book. Tablets and up are unchanged. */}
      <main className="flex min-h-0 flex-1 flex-col gap-px max-md:overflow-y-auto">{children}</main>
      {bottomBar ? (
        <div
          // **`lg:hidden`, not `md:hidden`.** The bar is the only way to open the
          // ticket sheet, and the ticket loses its column at `lg` (1120px) — not
          // at `md`. Hiding the bar from 768px up therefore left the whole
          // 768–1119px band with no order entry at all: no column, a sheet, and
          // nothing anywhere to open it with. components.md §1 is explicit that
          // this band's ticket is "a bottom sheet **triggered by the UP/DOWN
          // buttons**", so the bar has to survive as long as the sheet is the
          // only ticket there is. Invisible until Phase 6 put a real ticket
          // behind it (register §F23d).
          className="shrink-0 border-t border-border-subtle bg-bg-surface px-3 pt-2 lg:hidden"
          style={{ paddingBottom: 'calc(var(--ud-space-2) + env(safe-area-inset-bottom))' }}
        >
          {bottomBar}
        </div>
      ) : null}
    </div>
  )
}
